/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { dateCache } from '@touchplan/common';
import { PLAN_CONSTANTS } from '@touchplan/common';
import { shiftWeekStartToMonday, concatMaps } from '@touchplan/common';

function nextDow(num: number): number {
	return (num + 1) % 7;
}

function previousDow(dayOfWeek: number): number {
	return (dayOfWeek - 1 + 7) % 7;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
interface WorkdayConfig {
	requireNextDayToBeAWorkDay?: boolean;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
interface WorkdayMetadata {
	liveDurationDays: number;
	liveWorkingDays: boolean[];
	width?: number;
	liveFinish?: string;
}

// role day of week
// eslint-disable-next-line @typescript-eslint/naming-convention
interface RoleDows {
	[key: string]: number[];
}

export interface IMetadataForDateRangeResponse {
	liveDurationDays: number;
	width: number;
	liveFinish: string;
	liveStart: string;
	liveWorkingDays: boolean[];
	liveExceptionDays: Map<string, boolean>;
}

export const defaultWorkdays: number[] = [1, 1, 1, 1, 1, 1, 1];

// potential optimization:
// - precalculate a large workdays array (for each role)
// -- based on guessed size or something?
// - keep track of it's total contiguous size by updating it's start and finish date
// - when a request fit's, just use that
// - when it doesn't, precalculate to that requests dates + some extra
// - when any work day property changes, throw out the whole cache and start over

//not going to bother making this generic/ extensible, is a pretty simple/ specific thing
export class CalendarWorkdaysCalc {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private internalRoleDows: RoleDows = {};
	// eslint-disable-next-line
	private workByRole: any = {};

	//map the external function onto the instance for legacy purposes
	// eslint-disable-next-line @typescript-eslint/typedef
	public generateLocalExceptionDays = generateLocalExceptionDays;

	public exceptionDays: Map<string, boolean>;

	/**
	 * Set and recalculate all days of week for the role.
	 * Dow - day of week.
	 *
	 * @param dow - RoleDows, object mapping of role names to day of week number arrays
	 */
	// eslint-disable-next-line accessor-pairs
	public set roleDows(dow: RoleDows) {
		this.internalRoleDows = dow;
		this.recalcAllRoleDow();
	}

	private _projectWorkdays: number[];

	/**
	 * Set project workdays
	 *
	 * @param workdays - array of numbers
	 */
	// eslint-disable-next-line accessor-pairs
	public set projectWorkdays(workdays: number[]) {
		this._projectWorkdays = workdays;
		this.recalcAllRoleDow();
	}

	/**
	 * update a subset of days of week for the given role, mainly for the observable version
	 * CONSIDER THIS DEPRECATED WITHOUT FURTHER UPDATES
	 * @param dows - RoleDows, array of numbers
	 */
	public updateRowDows(dows: RoleDows): void {
		for (const [id, dow] of Object.entries(dows)) {
			this.internalRoleDows[id] = dow;
			this.recalcRoleDow(id, dow);
		}
	}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	public constructor(
		projectWorkdays: number[] = defaultWorkdays,
		exceptionDays: Map<string, boolean> = new Map(),
		roleDows: RoleDows = {}
	) {
		this._projectWorkdays = projectWorkdays;
		this.internalRoleDows = roleDows;
		this.exceptionDays = exceptionDays;
		this.recalcAllRoleDow();
	}

	protected recalcAllRoleDow(): void {
		// eslint-disable-next-line guard-for-in
		for (const key in this.internalRoleDows) {
			this.recalcRoleDow(key, this.internalRoleDows[key]);
		}
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private recalcRoleDow(roleKey: string, roleDow: number[]): void {
		if (roleDow) {
			this.workByRole[roleKey] = roleDow;
		} else {
			this.workByRole[roleKey] = this._projectWorkdays;
		}
	}

	// This method accepts positive and negative day offsets.
	// protected to allow for unit testing
	// eslint-disable-next-line @typescript-eslint/naming-convention
	protected isException(start: string, dayOffset: number): boolean {
		const res = dateCache.addDays(start, dayOffset);
		return this.exceptionDays.has(res);
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private isSubstractException(start: string, dayOffset: number): boolean {
		const res = dateCache.substractDays(start, dayOffset);
		return this.exceptionDays.has(res) ? !this.exceptionDays.get(res) : false;
	}

	// it is possible that we request this information for deleted role
	// eslint-disable-next-line
	private getCurrentWorkdaysForRole(roleId?: string): any {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return this.workByRole[roleId as any] || this._projectWorkdays;
	}

	/**
	 * Update a subset of the role dows, within the given date range.
	 * This is mainly used for the observable version.
	 *
	 * @param startDate start date
	 * @param durationDays workday duration
	 * @param roleId role id
	 * @param allowNonworkStart whether starting from a non-workday is allowed, defaults to false
	 * @returns number of workdays
	 * @remarks Used by Desktop app TicketDragService
	 */
	public getWorkdaysWithinRange(
		startDate: string,
		durationDays: number,
		roleId?: string,
		allowNonworkStart: boolean = false
	): number {
		if (durationDays === 0) {
			return 0;
		}

		if (!durationDays) {
			throw new Error('duration is no defined');
		}

		// early out if current workdays is not defined
		let currentWorkdays;
		if (roleId) {
			currentWorkdays = this.workByRole[roleId];
		} else {
			currentWorkdays = this._projectWorkdays;
		}
		if (!currentWorkdays) {
			return 0;
		}

		let count = 0;
		let workDayCount = 0;
		let date = startDate;
		while (count < durationDays) {
			date = dateCache.addDays(startDate, count);
			const isWorking = this.isWorkday(date, roleId);

			if (isWorking) {
				workDayCount++;
			}
			//allow the first day to start on non-work days
			if (count === 0 && !isWorking && allowNonworkStart) {
				workDayCount++;
			}
			count++;
		}
		return workDayCount;
	}

	/**
	 * Get calendar offset, used primarily to convert the relative start workday offset from the left edge of the drag to the live position based representation.
	 * Returns 0 offsets if durationDays is 0. This is used by TicketDragService.
	 *
	 * @param startDate start date
	 * @param durationDays workday duration
	 * @param roleId role id
	 * @param allowNonworkStart whether starting from a non-workday is allowed, defaults to true
	 * @returns object contains start offset days & calendar days
	 * @remarks Used by Desktop app TicketDragService
	 */
	public getCalendarOffset(
		startDate: string,
		durationDays: number,
		roleId: string,
		allowNonworkStart: boolean = true
	): { startOffsetDays: number; calendarDays: number } {
		return this.internalWorkDayCalc(startDate, durationDays, roleId, allowNonworkStart, {
			requireNextDayToBeAWorkDay: true,
		});
	}

	/**
	 * Get workday representation. Calculate relative start workday offset from the left edge of the drag.
	 * This is used by TicketDragService.
	 *
	 * @param startDate start date
	 * @param durationDays workday duration
	 * @param roleId role id
	 * @param allowNonworkStart whether starting from a non-workday is allowed, defaults to false
	 * @returns object contains start offset days & calendar days
	 * @remarks Used by Desktop app TicketDragService
	 */
	// there's potential to build in a cache here, start without
	public getWorkdayRepresentation(
		startDate: string,
		durationDays: number,
		roleId: string,
		allowNonworkStart: boolean = false
	): { startOffsetDays: number; calendarDays: number } {
		return this.internalWorkDayCalc(startDate, durationDays, roleId, allowNonworkStart);
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private internalWorkDayCalc(
		startDate: string,
		durationDays: number,
		roleId?: string,
		allowNonworkStart: boolean = false,
		specialConfig: WorkdayConfig = {}
	): { startOffsetDays: number; calendarDays: number } {
		const currentWorkdays = this.getCurrentWorkdaysForRole(roleId);
		//special check for the leftmost start offset check
		if (durationDays === 0) {
			return { startOffsetDays: 0, calendarDays: 0 };
		}
		if (!durationDays) {
			throw new Error('duration is no defined');
		}
		//if nothings loaded, everything is a valid work day
		if (!currentWorkdays) {
			return { startOffsetDays: 0, calendarDays: durationDays };
		}

		//TODO likely the best method will be to calculate some day offsets to minimize the moment date queries
		let haveFoundFirstWorkDay = false;
		let startOffsetDays = 0;
		let workDayCount = 0;
		let calendarDays = 0;

		// debugger;
		let currentDate = startDate;
		let isWorking = this.isWorkday(currentDate, roleId);

		while (workDayCount < durationDays || (specialConfig.requireNextDayToBeAWorkDay && !isWorking)) {
			if (allowNonworkStart && !haveFoundFirstWorkDay) {
				haveFoundFirstWorkDay = true;
				workDayCount++;
			} else if (haveFoundFirstWorkDay) {
				if (isWorking) {
					workDayCount++;
				}
			} else {
				if (isWorking) {
					haveFoundFirstWorkDay = true;
					workDayCount++;
				} else {
					startOffsetDays++;
				}
			}
			calendarDays++;
			currentDate = dateCache.addDays(startDate, calendarDays);
			isWorking = this.isWorkday(currentDate, roleId);
		}
		return { startOffsetDays, calendarDays: calendarDays - startOffsetDays };
	}

	//new stuff

	/**
	 * Add workdays to a given start date, option to override with exception days.
	 * It calculates number of calendar days need to be added to workdays and uses date cache to calculate new workdays
	 *
	 * This function operates in the startDate -> endDate semantics. So adding 1 day will return the same date because it's a finish date now
	 *
	 * @param startDate start date
	 * @param durationDays workday duration
	 * @param roleId role id
	 * @param exceptionDaysOverride override workdays with exception days
	 * @returns new workday date with given number of days added
	 */
	public addWorkingDaysToDate(
		startDate: string,
		// eslint-disable-next-line @rushstack/no-new-null
		durationDays?: number | null,
		roleId?: string,
		exceptionDaysOverride?: Map<string, boolean>
	): string {
		//adding nothing should return 0...
		if (durationDays === 0) {
			return startDate;
		}

		//exceptional case handling
		if (durationDays === undefined || durationDays === null) {
			throw new Error('duration must be defined');
		}
		if (durationDays < 0) {
			throw new Error('cannot add negative number, will need to add a subtract function');
		}

		let dayOfWeek = shiftWeekStartToMonday(dateCache.getDow(startDate));
		let calendarDays = -1;

		const clonedExceptions = new Map(exceptionDaysOverride);

		let currentDate = startDate;
		while (durationDays > 0) {
			currentDate = dateCache.addDays(startDate, calendarDays + 1);
			let isWorking = this.isWorkday(currentDate, roleId);

			//handle override
			if (clonedExceptions && clonedExceptions.has(currentDate)) {
				isWorking = clonedExceptions.get(currentDate) || false;
			}

			if (isWorking) {
				durationDays--;
			}

			dayOfWeek = nextDow(dayOfWeek);
			calendarDays++;
		}

		return dateCache.addDays(startDate, calendarDays);
	}

	/**
	 * Subtract workdays from a given finish date, option to override with exception days.
	 *
	 * @param finishDate finish date
	 * @param durationDays workday duration to subtract
	 * @param roleId role id
	 * @param exceptionDaysOverride override workdays with exception days
	 * @returns new workday date with given number of days subtracted
	 */
	public subtractWorkingDaysFromDate(
		finishDate: string,
		durationDays?: number,
		roleId?: string,
		exceptionDaysOverride?: Map<string, boolean>
	): string {
		const currentWorkdays = this.getCurrentWorkdaysForRole(roleId);

		// subtracting nothing should return the same start date
		if (durationDays === 0) {
			return finishDate;
		}

		// exceptional case handling
		if (durationDays === undefined) {
			throw new Error('duration must be defined');
		}
		if (durationDays < 0) {
			throw new Error('cannot subtract a negative number');
		}

		let dayOfWeek = shiftWeekStartToMonday(dateCache.getDow(finishDate));
		let calendarDays = -1;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const clonedExceptions = new Map(exceptionDaysOverride as any);

		while (durationDays > 0) {
			let isWorking = currentWorkdays[dayOfWeek] && !this.isSubstractException(finishDate, calendarDays + 1);

			// handle override
			const currentDate = dateCache.substractDays(finishDate, calendarDays + 1);
			if (clonedExceptions && clonedExceptions.has(currentDate)) {
				isWorking = clonedExceptions.get(currentDate);
			}

			if (isWorking) {
				durationDays--;
			}

			dayOfWeek = previousDow(dayOfWeek);
			calendarDays++;
		}

		return dateCache.substractDays(finishDate, calendarDays);
	}

	/**
	 * Calculate workdays between given start and end date, option to be overridden by exception days
	 *
	 * @param startDate start date
	 * @param finishDate workday duration
	 * @param roleId role id
	 * @param exceptionDaysOverride override workdays with exception days
	 * @param skipStartDateAutoExceptions skip start date auto exceptions
	 * @returns array of calculated workdays
	 */
	public calculateWorkdays(
		startDate: string,
		finishDate: string,
		roleId?: string,
		exceptionDaysOverride?: Map<string, boolean>,
		skipStartDateAutoExceptions?: boolean
	): boolean[] {
		let dayOfWeek = shiftWeekStartToMonday(dateCache.getDow(startDate));
		const workdays: boolean[] = [];
		let calendarDays = 0;

		//force the first day to be a working day (unless skipStartDateAutoExceptions is true)
		const clonedExceptions = new Map(exceptionDaysOverride);
		if (!skipStartDateAutoExceptions) {
			concatMaps(clonedExceptions, new Map([[startDate, true]]));
		}

		const calendarLength = dateCache.diffDays(finishDate, startDate);
		let currentDate = startDate;
		for (let i = calendarLength; i >= 0; i--) {
			currentDate = dateCache.addDays(startDate, calendarDays);
			let isWorking = this.isWorkday(currentDate, roleId);

			//handle override
			if (clonedExceptions && clonedExceptions.has(currentDate)) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				isWorking = clonedExceptions.get(currentDate) as any;
			}

			workdays.push(isWorking);
			calendarDays++;
			dayOfWeek = nextDow(dayOfWeek);
		}
		return workdays;
	}

	/**
	 * Check if given date is workday based on role id
	 *
	 * @param startDate start date
	 * @param roleId role id
	 * @returns boolean indicates if it's workday
	 */
	public isWorkday(startDate: string, roleId?: string): boolean {
		const currentWorkdays = this.getCurrentWorkdaysForRole(roleId);

		const dayOfWeek = shiftWeekStartToMonday(dateCache.getDow(startDate));

		if (this.isException(startDate, 0)) {
			const exceptionDay = this.exceptionDays.get(startDate);
			// if exception day is a boolean, then use the value directly. Otherwise the presence of an object indicates the
			// specified date is not a work day.
			return typeof exceptionDay === 'boolean' ? exceptionDay : false;
		}

		return !!currentWorkdays[dayOfWeek];
	}

	/**
	 * Calculate workdays properties with set start date & duration.
	 * The calculation treats weekends as non-workdays unless overridden by exception days.
	 *
	 * @param liveExceptionDays exception days map with string-boolean pair, with date being key
	 * @param roleId role id
	 * @param liveStart live start date
	 * @param duration number of workdays
	 * @returns object contains updated workday properties
	 */
	public getMetadataForDuration(
		liveExceptionDays: Map<string, boolean> | undefined,
		roleId: string | undefined,
		liveStart: string,
		duration: number
	): {
		liveDurationDays: number;
		width: number | PLAN_CONSTANTS;
		liveFinish: string;
		liveWorkingDays: boolean[];
		liveExceptionDays: Map<string, boolean>;
	} {
		// Process exception days
		// If the map is undefined, creates a new one with the start date as true
		// If the map is defined, sets the start date as true
		const processedExceptions = this.processExceptionDaysMap(liveExceptionDays, liveStart);

		const finishDate = this.addWorkingDaysToDate(liveStart, duration, roleId, processedExceptions);
		const workdays = this.calculateWorkdays(liveStart, finishDate, roleId, processedExceptions);
		const width = this.getWidthFromWorkdays(workdays.length) || PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH; // enforce minimum width

		return {
			liveDurationDays: duration,
			width,
			liveFinish: finishDate,
			liveWorkingDays: workdays,
			liveExceptionDays: this.capExceptionDays(processedExceptions, liveStart, finishDate),
		};
	}

	/**
	 * Calculates workdays properties with given start & finish date range
	 * The calculation treats weekends as non-workdays unless overridden by exception days.
	 *
	 * @param liveExceptionDays exception days map with string-boolean pair, with date being key
	 * 		Note: this parameter is not used in the isWorkday function, which uses this.exceptionDays instead.
	 * @param roleId role id
	 * @param liveStart live start date
	 * @param liveFinish live finish date
	 * @returns object contains updated workdays properties
	 */
	public getMetadataForDateRange(
		liveExceptionDays: Map<string, boolean>,
		roleId: string | undefined, //TODO - this property should be optional, but doing so requires moving it to the final arg
		liveStart: string,
		liveFinish: string
	): IMetadataForDateRangeResponse {
		const exceptions = this.getExceptions(liveExceptionDays, liveStart, liveFinish);
		const workdays = this.calculateWorkdays(liveStart, liveFinish, roleId, exceptions);
		const duration = this.getDurationFromWorkdays(workdays);
		const width = this.getWidthFromWorkdays(workdays.length);

		// NOTE: returned object doesn't contain "left", as it's not needed by mobile team
		return {
			liveDurationDays: duration,
			width,
			liveFinish,
			liveStart,
			liveWorkingDays: workdays,
			liveExceptionDays: exceptions,
		};
	}

	/**
	 * Calculates/updates workdays properties with given start date and optional finish date
	 *
	 * @param workdays array indicates if each day is a workday
	 * @param liveExceptionDays exception days map with string-boolean pair, with date being key
	 * @param liveDurationDays live number of duration days
	 * @param roleId role id
	 * @param liveStart live start date
	 * @param useFixedEndDate boolean flag indicating if workdays calculation has be set to use FixedEnd instead of DurationFixed
	 * @returns object contains updated workday properties
	 */
	public getMetadataForWorkdays(
		workdays: boolean[],
		liveExceptionDays: Map<string, boolean>,
		liveDurationDays: number,
		roleId: string,
		liveStart: string,
		useFixedEndDate: boolean
	): WorkdayMetadata {
		//if the first one is off, just flip it back on
		if (workdays.length && !workdays[0]) {
			workdays[0] = true;
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const output: any = useFixedEndDate
			? this.processWorkdaysFixedEnd(workdays)
			: this.processWorkdaysDurationFixed(workdays, liveExceptionDays, roleId, liveStart, liveDurationDays);
		const newExceptions = this.generateLocalExceptionDays(liveStart, workdays);
		output.liveExceptionDays = newExceptions;

		return output;
	}

	/**
	 * Update exceptions days if start or finish dates are non-workdays
	 *
	 * @param liveExceptionDays exception days map with string-boolean pair, with date being key
	 * 		Note: This parameter is not used in the isWorkday function, which uses this.exceptionDays instead.
	 * @param liveStart live start date
	 * @param liveFinish live finish date
	 * @returns updated exception map
	 */
	public getExceptions(
		liveExceptionDays: Map<string, boolean>,
		liveStart: string,
		liveFinish: string
	): Map<string, boolean> {
		const isStartNonWork = !this.isWorkday(liveStart);
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7413
		// this function should use the passed in liveExceptionDays to determine if the finish date is a workday
		const isEndNonWork = !this.isWorkday(liveFinish);
		// END PLACEHOLDER REPLACE AS PART OF TP-7413
		let exceptions = liveExceptionDays;

		if (isStartNonWork || isEndNonWork) {
			const clonedExceptions = new Map(exceptions);

			if (isStartNonWork) {
				clonedExceptions.set(liveStart, true);
			}

			if (isEndNonWork) {
				clonedExceptions.set(liveFinish, true);
			}

			exceptions = clonedExceptions;
		}

		exceptions = this.capExceptionDays(exceptions, liveStart, liveFinish);

		return exceptions;
	}

	/**
	 * Creates a new map based on a provided one and sets the start date as true (meaning it's a workday, because there is a requirement for the start date to be a workday always).
	 * @param liveExceptionDays - The exception days map.
	 * @param liveStart - The start date of the ticket.
	 * @returns A map containing the exception days.
	 */
	public processExceptionDaysMap(
		liveExceptionDays: Map<string, boolean> | undefined,
		liveStart: string
	): Map<string, boolean> {
		const clonedExceptions = new Map(liveExceptionDays);
		clonedExceptions.set(liveStart, true);
		return clonedExceptions;
	}

	/**
	 * Cap each exception day if they aren't within given start date and end date
	 *
	 * @param exceptions source exception days
	 * @param startDate start date
	 * @param endDate end date
	 * @returns new capped exception days
	 */
	public capExceptionDays(
		exceptions: Map<string, boolean>,
		startDate: string,
		endDate: string
	): Map<string, boolean> {
		const newExceptions = new Map<string, boolean>();
		exceptions.forEach((setting, date) => {
			if (date >= startDate && date <= endDate) {
				newExceptions.set(date, setting);
			}
		});
		return newExceptions;
	}

	/**
	 * Accumulate/sum all workdays using Array reducer
	 *
	 * @param workdays workdays array
	 * @returns total workdays
	 */
	public getDurationFromWorkdays(workdays: boolean[]): number {
		return workdays.reduce((acc, curr) => (acc += curr ? 1 : 0), 0);
	}

	/**
	 * Calculate ticket width with given number of workdays. It currently calculates using ACTIVE_COLUMN_WIDTH of 145px
	 *
	 * @param numWorkdays number of workdays
	 * @returns width of ticket
	 */
	public getWidthFromWorkdays(numWorkdays: number): number {
		return numWorkdays * PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH;
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private processWorkdaysFixedEnd(workdays: boolean[]): {
		liveDurationDays: number;
		liveWorkingDays: boolean[];
	} {
		let derivedWorkDays = this.getDurationFromWorkdays(workdays);

		//if the last one is off, just flip it back on
		if (workdays.length > 1 && !workdays[workdays.length - 1]) {
			workdays[workdays.length - 1] = true;
			derivedWorkDays++;
		}

		return {
			liveDurationDays: derivedWorkDays,
			liveWorkingDays: workdays,
		};
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private processWorkdaysDurationFixed(
		workdays: boolean[],
		liveExceptionDays: Map<string, boolean>,
		roleId: string,
		liveStart: string,
		liveDurationDays: number
	): { liveDurationDays: number; width: number; liveFinish: string; liveWorkingDays: boolean[] } {
		const derivedWorkingDays = this.getDurationFromWorkdays(workdays);
		const diff = derivedWorkingDays - liveDurationDays;

		//added days
		if (diff > 0) {
			let removedWorkdays = 0;
			//remove days from workdays by slicing the array
			for (let i = workdays.length - 1; i >= 0; i--) {
				const day = workdays[i];
				if (day && removedWorkdays < diff) {
					workdays.pop();
					++removedWorkdays;
					continue;
				}

				if (removedWorkdays >= diff) {
					if (!day) {
						workdays.pop();
						continue;
					} else {
						break;
					}
				}
			}
		} else if (diff < 0) {
			//removed days
			//add days via calendar workDays to get the new finish
			//use calculateWorkdays to get the new workdays
			const startOfNewRange = dateCache.addDays(liveStart, workdays.length);
			const finishOfNewRange = this.addWorkingDaysToDate(
				startOfNewRange,
				Math.abs(diff),
				roleId,
				liveExceptionDays
			);

			//make sure to skip auto-exceptioning the startDate
			const theNextDays = this.calculateWorkdays(
				startOfNewRange,
				finishOfNewRange,
				roleId,
				liveExceptionDays,
				true
			);

			Array.prototype.push.apply(workdays, theNextDays);
		}

		const duration = this.getDurationFromWorkdays(workdays);
		const width = this.getWidthFromWorkdays(workdays.length);
		const finishDate = dateCache.addDays(liveStart, workdays.length - 1);

		return {
			liveDurationDays: duration,
			width,
			liveFinish: finishDate,
			liveWorkingDays: workdays,
		};
	}

	/**
	 * Calculate the duration (in workdays) for a given number of weeks.
	 * If a role is provided, it calculates the duration based on the role's workdays;
	 * otherwise, it uses the project's default workdays.
	 *
	 * @param weeks - The number of weeks to calculate.
	 * @param roleId - The ID of the role to get specific workdays.
	 * @returns The total duration in workdays (rounded to the nearest whole number).
	 */
	public calculateWorkdaysFromWeeks(weeks: number, roleId?: string): number {
		const workdays = this.getCurrentWorkdaysForRole(roleId);
		const workdaysPerWeek = workdays.filter((isWorkday: number) => !!isWorkday).length;
		return weeks * workdaysPerWeek;
	}

	public destroy(): void {}
}

//this can be external, so it is
export function generateLocalExceptionDays(startDate: string, workingDays: boolean[]): Map<string, boolean> {
	const thing = new Map<string, boolean>();
	workingDays.forEach((day, dayNum) => {
		thing.set(dateCache.addDays(startDate, dayNum), day);
	});
	return thing;
}
